// Stripe Error Handler - Maps Stripe error codes to user-friendly messages
export const STRIPE_ERROR_TYPES = {
  CARD_ERROR: 'card_error',
  VALIDATION_ERROR: 'validation_error',
  API_ERROR: 'api_error',
  AUTHENTICATION_ERROR: 'authentication_error',
  RATE_LIMIT_ERROR: 'rate_limit_error',
  INVALID_REQUEST_ERROR: 'invalid_request_error',
};

export const STRIPE_ERROR_CODES = {
  // Card declined errors
  CARD_DECLINED: 'card_declined',
  INSUFFICIENT_FUNDS: 'insufficient_funds',
  LOST_CARD: 'lost_card',
  STOLEN_CARD: 'stolen_card',
  EXPIRED_CARD: 'expired_card',
  INCORRECT_CVC: 'incorrect_cvc',
  PROCESSING_ERROR: 'processing_error',
  INCORRECT_NUMBER: 'incorrect_number',
  INVALID_NUMBER: 'invalid_number',
  INVALID_EXPIRY_MONTH: 'invalid_expiry_month',
  INVALID_EXPIRY_YEAR: 'invalid_expiry_year',
  INVALID_CVC: 'invalid_cvc',

  // 3D Secure
  AUTHENTICATION_REQUIRED: 'authentication_required',

  // Network/API errors
  API_CONNECTION_ERROR: 'api_connection_error',
  API_ERROR: 'api_error',
};

export const getStripeErrorMessage = (error) => {
  const errorCode = error?.code;
  const declineCode = error?.decline_code;

  // Handle decline codes first
  if (declineCode) {
    switch (declineCode) {
      case 'insufficient_funds':
        return 'Your card has insufficient funds. Please use a different payment method.';
      case 'lost_card':
      case 'stolen_card':
        return 'This card has been reported as lost or stolen. Please use a different card.';
      case 'generic_decline':
        return 'Your card was declined. Please contact your bank or try a different card.';
      case 'do_not_honor':
        return 'Your card was declined. Please contact your bank for more information.';
      case 'fraudulent':
        return 'This transaction was flagged as potentially fraudulent. Please contact your bank.';
      default:
        return `Card declined: ${declineCode}. Please try a different payment method.`;
    }
  }

  // Handle error codes
  switch (errorCode) {
    case STRIPE_ERROR_CODES.CARD_DECLINED:
      return 'Your card was declined. Please check your card details or try a different card.';
    case STRIPE_ERROR_CODES.EXPIRED_CARD:
      return 'Your card has expired. Please use a different card.';
    case STRIPE_ERROR_CODES.INCORRECT_CVC:
    case STRIPE_ERROR_CODES.INVALID_CVC:
      return 'The security code (CVC) is incorrect. Please check and try again.';
    case STRIPE_ERROR_CODES.INCORRECT_NUMBER:
    case STRIPE_ERROR_CODES.INVALID_NUMBER:
      return 'The card number is invalid. Please check and try again.';
    case STRIPE_ERROR_CODES.INVALID_EXPIRY_MONTH:
    case STRIPE_ERROR_CODES.INVALID_EXPIRY_YEAR:
      return 'The expiration date is invalid. Please check and try again.';
    case STRIPE_ERROR_CODES.PROCESSING_ERROR:
      return 'An error occurred while processing your card. Please try again.';
    case STRIPE_ERROR_CODES.AUTHENTICATION_REQUIRED:
      return 'Additional authentication is required. Please complete the verification.';
    case STRIPE_ERROR_CODES.API_CONNECTION_ERROR:
      return 'Network error. Please check your connection and try again.';
    case STRIPE_ERROR_CODES.API_ERROR:
      return 'A payment processing error occurred. Please try again later.';
    default:
      return error?.message || 'Payment failed. Please try again or contact support.';
  }
};

export const isRetryableError = (error) => {
  const retryableCodes = [STRIPE_ERROR_CODES.API_CONNECTION_ERROR, STRIPE_ERROR_CODES.API_ERROR, STRIPE_ERROR_CODES.PROCESSING_ERROR];
  return retryableCodes.includes(error?.code);
};

// Test card numbers for Stripe testing
export const STRIPE_TEST_CARDS = {
  SUCCESS: '4242424242424242',
  DECLINED: '4000000000000002',
  INSUFFICIENT_FUNDS: '4000000000009995',
  LOST_CARD: '4000000000009987',
  STOLEN_CARD: '4000000000009979',
  EXPIRED_CARD: '4000000000000069',
  INCORRECT_CVC: '4000000000000127',
  PROCESSING_ERROR: '4000000000000119',
  REQUIRES_3DS: '4000002500003155',
};
